
import React, { useState, useEffect } from 'react';
import { getUserTypeDisplayName } from '@/utils/userTypeDisplay';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Users, UserCheck, Shield, Search } from 'lucide-react';
import { userService } from '@/services/chat/userService';
import { supabaseUserSearchService } from '@/services/chat/supabase/userSearchService';
import { User } from '@/types/chat';
import { supabase } from '@/integrations/supabase/client';

interface CoachConnectionsSectionProps {
  connectedPlayers: string[];
  connectedCoaches: string[];
  connectedTeams: string[];
  onAddConnection: (type: 'player' | 'coach' | 'team', identifier: string) => void;
  onRemoveConnection: (type: 'player' | 'coach' | 'team', identifier: string) => void;
}

export const CoachConnectionsSection: React.FC<CoachConnectionsSectionProps> = ({
  connectedPlayers,
  connectedCoaches,
  connectedTeams,
  onAddConnection,
  onRemoveConnection
}) => {
  const [newPlayerInput, setNewPlayerInput] = useState('');
  const [newCoachInput, setNewCoachInput] = useState('');
  const [newTeamInput, setNewTeamInput] = useState('');
  
  // Search states
  const [playerSearchResults, setPlayerSearchResults] = useState<User[]>([]);
  const [coachSearchResults, setCoachSearchResults] = useState<User[]>([]);
  const [teamSearchResults, setTeamSearchResults] = useState<any[]>([]);
  const [showPlayerResults, setShowPlayerResults] = useState(false);
  const [showCoachResults, setShowCoachResults] = useState(false);
  const [showTeamResults, setShowTeamResults] = useState(false);
  const [searchingPlayers, setSearchingPlayers] = useState(false);
  const [searchingCoaches, setSearchingCoaches] = useState(false);
  const [searchingTeams, setSearchingTeams] = useState(false);

  // Search for players
  const searchPlayers = async (query: string) => {
    if (query.length < 2) {
      setPlayerSearchResults([]);
      setShowPlayerResults(false);
      return;
    }

    setSearchingPlayers(true);
    try {
      console.log('Searching for players with query:', query);
      
      // Search both internal and Supabase users
      const [internalUsers, supabaseUsers] = await Promise.all([
        userService.searchUsers(query),
        supabaseUserSearchService.searchUsers(query)
      ]);

      console.log('Internal users found:', internalUsers);
      console.log('Supabase users found:', supabaseUsers);

      // Filter for players only
      const allUsers = [...internalUsers, ...supabaseUsers];
      const players = allUsers.filter(user => user.role === 'player');
      
      console.log('Players filtered:', players);
      
      setPlayerSearchResults(players);
      setShowPlayerResults(true);
    } catch (error) {
      console.error('Error searching players:', error);
      setPlayerSearchResults([]);
    } finally {
      setSearchingPlayers(false);
    }
  };

  // Search for coaches
  const searchCoaches = async (query: string) => {
    if (query.length < 2) {
      setCoachSearchResults([]);
      setShowCoachResults(false);
      return;
    }

    setSearchingCoaches(true);
    try {
      // Search both internal and Supabase users
      const [internalUsers, supabaseUsers] = await Promise.all([
        userService.searchUsers(query),
        supabaseUserSearchService.searchUsers(query)
      ]);

      // Filter for coaches only
      const allUsers = [...internalUsers, ...supabaseUsers];
      const coaches = allUsers.filter(user => user.role === 'coach');
      
      setCoachSearchResults(coaches);
      setShowCoachResults(true);
    } catch (error) {
      console.error('Error searching coaches:', error);
      setCoachSearchResults([]);
    } finally {
      setSearchingCoaches(false);
    }
  };

  // Handle player input change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (newPlayerInput) {
        searchPlayers(newPlayerInput);
      } else {
        setShowPlayerResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [newPlayerInput]);

  // Handle coach input change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (newCoachInput) {
        searchCoaches(newCoachInput);
      } else {
        setShowCoachResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [newCoachInput]);

  const handleAddPlayer = (player?: User) => {
    const playerToAdd = player ? player.username : newPlayerInput.trim();
    if (playerToAdd && !connectedPlayers.includes(playerToAdd)) {
      onAddConnection('player', playerToAdd);
      setNewPlayerInput('');
      setShowPlayerResults(false);
    }
  };

  const handleAddCoach = (coach?: User) => {
    const coachToAdd = coach ? coach.username : newCoachInput.trim();
    if (coachToAdd && !connectedCoaches.includes(coachToAdd)) {
      onAddConnection('coach', coachToAdd);
      setNewCoachInput('');
      setShowCoachResults(false);
    }
  };

  const handleAddTeam = (team?: any) => {
    const teamToAdd = team ? team.team_name : newTeamInput.trim();
    if (teamToAdd && !connectedTeams.includes(teamToAdd)) {
      onAddConnection('team', teamToAdd);
      setNewTeamInput('');
      setShowTeamResults(false);
    }
  };

  // Team search functionality
  const searchTeams = async (query: string) => {
    if (query.length < 2) {
      setTeamSearchResults([]);
      setShowTeamResults(false);
      return;
    }

    setSearchingTeams(true);
    try {
      console.log('Searching for teams with query:', query);
      
      const { data, error } = await supabase
        .from('teams')
        .select('team_id, team_name, sport_type, location')
        .ilike('team_name', `%${query}%`)
        .limit(10);

      if (error) {
        console.error('Error searching teams:', error);
        setTeamSearchResults([]);
        setShowTeamResults(false);
        return;
      }

      console.log('Team search results:', data);
      setTeamSearchResults(data || []);
      setShowTeamResults((data || []).length > 0);
    } catch (error) {
      console.error('Error searching teams:', error);
      setTeamSearchResults([]);
      setShowTeamResults(false);
    } finally {
      setSearchingTeams(false);
    }
  };

  useEffect(() => {
    if (!newTeamInput.trim()) {
      setTeamSearchResults([]);
      setShowTeamResults(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchTeams(newTeamInput);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [newTeamInput]);

  return (
    <Card className="bg-gray-700 border-gray-600">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <Users className="h-5 w-5" />
          Professional Connections
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connected Players */}
        <div>
          <Label className="text-gray-300 flex items-center gap-2 mb-3">
            <UserCheck className="h-4 w-4" />
            Connected Players
          </Label>
          <div className="relative">
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Input
                  value={newPlayerInput}
                  onChange={(e) => setNewPlayerInput(e.target.value)}
                  placeholder="Search for players by username..."
                  className="bg-gray-600 border-gray-500 text-white"
                />
                {searchingPlayers && (
                  <Search className="absolute right-3 top-3 h-4 w-4 text-gray-400 animate-spin" />
                )}
                
                {/* Player search results dropdown */}
                {showPlayerResults && playerSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-gray-600 border border-gray-500 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {playerSearchResults.map((player) => (
                      <button
                        key={`${player.user_type || 'unknown'}-${player.id}`}
                        onClick={() => handleAddPlayer(player)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-500 text-white border-b border-gray-500 last:border-b-0"
                        disabled={connectedPlayers.includes(player.username)}
                      >
                        <div className="flex items-center justify-between">
                          <span>{player.username}</span>
                          <span className="text-xs text-gray-400">
                            {getUserTypeDisplayName(player.user_type)}
                          </span>
                        </div>
                        {player.first_name && (
                          <div className="text-sm text-gray-400">
                            {player.first_name} {player.last_name}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                onClick={() => handleAddPlayer()} 
                size="sm" 
                className="bg-sports-green text-black hover:bg-green-600"
                type="button"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {connectedPlayers.map((player) => (
              <Badge key={player} variant="secondary" className="bg-blue-600 text-white">
                {player}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => onRemoveConnection('player', player)}
                />
              </Badge>
            ))}
          </div>
        </div>

        {/* Connected Coaches */}
        <div>
          <Label className="text-gray-300 flex items-center gap-2 mb-3">
            <Shield className="h-4 w-4" />
            Connected Coaches
          </Label>
          <div className="relative">
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Input
                  value={newCoachInput}
                  onChange={(e) => setNewCoachInput(e.target.value)}
                  placeholder="Search for coaches by username..."
                  className="bg-gray-600 border-gray-500 text-white"
                />
                {searchingCoaches && (
                  <Search className="absolute right-3 top-3 h-4 w-4 text-gray-400 animate-spin" />
                )}
                
                {/* Coach search results dropdown */}
                {showCoachResults && coachSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-gray-600 border border-gray-500 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {coachSearchResults.map((coach) => (
                      <button
                        key={`${coach.user_type || 'unknown'}-${coach.id}`}
                        onClick={() => handleAddCoach(coach)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-500 text-white border-b border-gray-500 last:border-b-0"
                        disabled={connectedCoaches.includes(coach.username)}
                      >
                        <div className="flex items-center justify-between">
                          <span>{coach.username}</span>
                          <span className="text-xs text-gray-400">
                            {getUserTypeDisplayName(coach.user_type)}
                          </span>
                        </div>
                        {coach.first_name && (
                          <div className="text-sm text-gray-400">
                            {coach.first_name} {coach.last_name}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                onClick={() => handleAddCoach()} 
                size="sm" 
                className="bg-sports-green text-black hover:bg-green-600"
                type="button"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {connectedCoaches.map((coach) => (
              <Badge key={coach} variant="secondary" className="bg-purple-600 text-white">
                {coach}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => onRemoveConnection('coach', coach)}
                />
              </Badge>
            ))}
          </div>
        </div>

        {/* Connected Teams */}
        <div>
          <Label className="text-gray-300 flex items-center gap-2 mb-3">
            <Users className="h-4 w-4" />
            Connected Teams
          </Label>
          <div className="relative">
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Input
                  value={newTeamInput}
                  onChange={(e) => setNewTeamInput(e.target.value)}
                  placeholder="Search for teams by name..."
                  className="bg-gray-600 border-gray-500 text-white"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTeam();
                    }
                  }}
                />
                {searchingTeams && (
                  <Search className="absolute right-3 top-3 h-4 w-4 text-gray-400 animate-spin" />
                )}
                
                {/* Team search results dropdown */}
                {showTeamResults && teamSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-gray-600 border border-gray-500 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {teamSearchResults.map((team) => (
                      <button
                        key={team.team_id}
                        onClick={() => handleAddTeam(team)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-500 text-white border-b border-gray-500 last:border-b-0"
                        disabled={connectedTeams.includes(team.team_name)}
                      >
                        <div className="flex items-center justify-between">
                          <span>{team.team_name}</span>
                          <span className="text-xs text-gray-400">
                            {team.sport_type || 'Team'}
                          </span>
                        </div>
                        {team.location && (
                          <div className="text-sm text-gray-400">
                            {team.location}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                onClick={() => handleAddTeam()} 
                size="sm" 
                className="bg-sports-green text-black hover:bg-green-600"
                type="button"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {connectedTeams.map((team) => (
              <Badge key={team} variant="secondary" className="bg-orange-600 text-white">
                {team}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => onRemoveConnection('team', team)}
                />
              </Badge>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
